import { useEffect, useRef } from "react";
import {
  defineInfiniteScrollAds,
  renderInfiniteScrollAdsBatch,
} from "@sphtech/web2-ads";
import { cn } from "@util/helpers";

import {
  BT_GAM_NETWORK_ID,
  CANARY_INFINITE_SCROLL_AD_SIZES,
  SDK_AD_SLOT_HOLDER_BASE_CLASS,
  SDK_AD_SLOT_RESERVE_HEIGHTS,
} from "./utils/constants";
import { AdUnitNames } from "./utils/types";

type CanaryInfiniteScrollAdSlotProps = {
  /** Ad placement, e.g. `imu1` / `imu2` / `midarticlespecial`. */
  adType: AdUnitNames;
  /** 0-based article index in the infinite-scroll stack (always > 0 here). */
  counter: number;
  /** Ad-unit page/section segment, from the latched canary config. */
  page: string;
  siteKey: string;
  /** Slot-level targeting (`btarticleid` / `bttags` / `gs_channels`). */
  targeting: Record<string, string | string[]>;
  /** DOM id of the GPT slot div, e.g. `dfp-ad-imu1-1`. */
  id: string;
  /** Slot instance key on `gadSlots` / `loaded_dfp_tags`, e.g. `imu1-1`. */
  slotName: string;
  /** Caller band/layout classes (rides onto the GPT slot div). */
  className?: string;
};

/**
 * Renders a single body ad for an *appended* (infinite-scroll) canary article
 * via the web2-ads SDK infinite-scroll primitives, instead of the declarative
 * `<AdSlot>` used for first-paint slots.
 *
 * `<AdSlot>` is not the SDK's sanctioned path for content added after the
 * initial page render: it skips the batched `loaded_dfp_tags` finalize and can
 * race / mis-finalize on dynamically-mounted slots. `defineInfiniteScrollAds`
 * registers the slot (with per-article targeting carried at slot level so it
 * cannot bleed onto other articles on refresh), and `renderInfiniteScrollAdsBatch`
 * finalizes the bridge once *inside* the GPT queue — after the queued define
 * write — so it is race-safe even for a single slot. This mirrors the legacy
 * `bt_ads.js` per-slot `display_ad(..., refresh=true)` flow.
 *
 * The infinite-scroll definition has no `reserveHeight` (unlike `<AdSlot>`), so
 * the holder owns its loading min-height for CLS, matching the legacy holder.
 */
export default function CanaryInfiniteScrollAdSlot({
  adType,
  counter,
  page,
  siteKey,
  targeting,
  id,
  slotName,
  className,
}: CanaryInfiniteScrollAdSlotProps): React.ReactElement {
  const definedIdRef = useRef<string | null>(null);

  useEffect(() => {
    // Define + render exactly once per slot `id`. Appended articles only
    // mount on scroll — well after the first article's contextual targeting
    // (gs_channels) has settled — so the props captured here are already final;
    // no `ready` gate is needed. SSR never runs this (counter > 0 is client
    // only, and effects do not run on the server). Tracking the defined `id`
    // (rather than a boolean) keeps the guard consistent with the `[id]` key: a
    // re-render of the same slot is a no-op, but if React ever reuses this
    // instance for a new `id` the new slot is still defined.
    if (definedIdRef.current === id) return;

    const sizes = CANARY_INFINITE_SCROLL_AD_SIZES[adType];
    if (!sizes) return;

    const slotIds = defineInfiniteScrollAds(
      [
        {
          networkId: BT_GAM_NETWORK_ID,
          sitePrefix: siteKey,
          placement: adType,
          page,
          slotId: id,
          slotName,
          sizes,
          targeting,
        },
      ],
      counter
    );

    renderInfiniteScrollAdsBatch(slotIds);
    definedIdRef.current = id;
    // Keyed on the unique slot `id`; remaining props are intentionally captured
    // at first mount (see comment above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // The infinite-scroll definition carries no SDK reservation, so reserve the
  // balanced loading band on the holder itself (body units are a flat base
  // height, no responsive breakpoints).
  const reserve = SDK_AD_SLOT_RESERVE_HEIGHTS[adType];
  const minHeight = typeof reserve === "object" ? reserve.base : undefined;

  return (
    <div
      id={id}
      data-testid={id}
      data-ad-type={adType}
      data-ad-index={counter}
      className={cn(
        className,
        SDK_AD_SLOT_HOLDER_BASE_CLASS,
        "flex justify-center"
      )}
      style={minHeight ? { minHeight } : undefined}
    />
  );
}
